import React, { useEffect, useState } from 'react';
import { Modal, Button, Input, Tooltip, Typography, Spin } from 'antd';
import { toast } from 'react-toastify';
import EmployeeFundService from '../../service/employee-service/Employee-fund.service';
import AccountBalanceService from '../../service/account-balance-service/Account-balance.service';
import { EmployeeFundUpdateDto } from '../../model/EmployeeFund.model';
import { PlusCircleOutlined } from '@ant-design/icons';

interface AddFundsModalProps {
  employeeId: number;
  onSuccess: () => void;
}

const AddFundsModal: React.FC<AddFundsModalProps> = ({
  employeeId,
  onSuccess,
}) => {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [employeeBalance, setEmployeeBalance] = useState<number | null>(null);
  const [companyBalance, setCompanyBalance] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchBalances = async () => {
    setLoading(true);
    try {
      const employeeFund =
        await EmployeeFundService.getFundByEmployeeId(employeeId);
      setEmployeeBalance(employeeFund.currentBalance);

      const companyFund = await AccountBalanceService.getAccountBalance();
      setCompanyBalance(companyFund.balanceAmount);
    } catch (error) {
      console.error('Error fetching balances:', error);
      toast.error('Không thể tải số dư. Vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchBalances();
    }
  }, [open]);

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value === '' || Number(value) === 0) {
      setAmount('');
      return;
    }
    setAmount(new Intl.NumberFormat('vi-VN').format(Number(value)));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!amount || Number(amount.replace(/\./g, '')) === 0) {
      toast.error('Vui lòng nhập số tiền lớn hơn 0');
      return;
    }
    setIsSubmitting(true);
    try {
      const dto: EmployeeFundUpdateDto = {
        currentBalance: Number(amount.replace(/\./g, '')),
      };
      await EmployeeFundService.updateFundForEmployee(employeeId, dto);
      toast.success('Nạp tiền thành công!');
      onSuccess();
      setOpen(false);
      setAmount(''); // Reset amount
    } catch (error) {
      console.error('Error adding funds:', error);
      toast.error('Không thể nạp tiền. Vui lòng thử lại.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Tooltip title="Nạp tiền">
        <PlusCircleOutlined
          className="cursor-pointer text-lg hover:text-blue-400"
          onClick={() => setOpen(true)}
        />
      </Tooltip>

      <Modal
        title="Nạp tiền vào quỹ"
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        width={450}
      >
        {loading ? (
          <div className="flex justify-center items-center py-4">
            <Spin size="large" />
          </div>
        ) : (
          <>
            <div className="space-y-4">
              <div>
                <Typography.Text strong>
                  Số dư quỹ của nhân viên:{' '}
                  {employeeBalance !== null
                    ? new Intl.NumberFormat('vi-VN').format(employeeBalance) +
                      ' VND'
                    : 'N/A'}
                </Typography.Text>
              </div>
              <div>
                <Typography.Text strong>
                  Số dư tài khoản của công ty:{' '}
                  {companyBalance !== null
                    ? new Intl.NumberFormat('vi-VN').format(companyBalance) +
                      ' VND'
                    : 'N/A'}
                </Typography.Text>
              </div>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Typography.Text strong>
                  Số tiền <span className="text-red-500">*</span>
                </Typography.Text>
                <Input
                  value={amount}
                  onChange={handleAmountChange}
                  required
                  placeholder="Nhập số tiền lớn hơn 0"
                  status={!amount ? 'error' : ''}
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button onClick={() => setOpen(false)}>Huỷ</Button>
                <Button type="primary" htmlType="submit" loading={isSubmitting}>
                  Nạp tiền
                </Button>
              </div>
            </form>
          </>
        )}
      </Modal>
    </>
  );
};

export default AddFundsModal;
